import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import { API_BASE } from '../../lib/api';

const API = API_BASE;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });

const EMPTY = { client_name: '', company: '', quote_es: '', quote_en: '', photo: '', active: true };

const AdminTestimonials = () => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const fetchItems = () => {
    axios.get(`${API}/admin/testimonials`, getAuth())
      .then(res => setItems(res.data))
      .catch(e => { if (e.response?.status === 401) navigate('/admin'); });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchItems(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (t) => { setEditing(t); setForm({ client_name: t.client_name, company: t.company, quote_es: t.quote_es, quote_en: t.quote_en, photo: t.photo || '', active: t.active }); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) { await axios.put(`${API}/admin/testimonials/${editing.id}`, form, getAuth()); }
      else { await axios.post(`${API}/admin/testimonials`, form, getAuth()); }
      fetchItems();
      setShowForm(false);
    } catch { alert('Error al guardar'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este testimonio?')) return;
    await axios.delete(`${API}/admin/testimonials/${id}`, getAuth());
    fetchItems();
  };

  return (
    <div className="p-8" data-testid="admin-testimonials-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-poppins font-bold text-2xl text-white">Testimonios</h1>
          <p className="text-white/40 text-sm font-source-sans mt-1">{items.length} testimonios</p>
        </div>
        <button onClick={openCreate} data-testid="create-testimonial-btn" className="flex items-center gap-2 bg-[#F2785C] text-white px-5 py-2.5 font-poppins font-semibold text-sm hover:bg-[#FF8F75] transition-colors">
          <Plus size={16} /> Nuevo Testimonio
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 ? (
          <div className="col-span-3 text-center py-16 text-white/30 font-source-sans">No hay testimonios aún.</div>
        ) : items.map(t => (
          <div key={t.id} data-testid={`testimonial-item-${t.id}`} className="bg-[#1A2639] border border-white/8 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#7D468C]/30 flex items-center justify-center flex-shrink-0" style={{ transform: 'rotate(45deg)' }}>
                  <span className="font-poppins font-bold text-white text-sm" style={{ transform: 'rotate(-45deg)' }}>{t.client_name[0]}</span>
                </div>
                <div>
                  <div className="font-poppins font-bold text-white text-sm">{t.client_name}</div>
                  <div className="text-[#4EBFBF] text-xs font-source-sans">{t.company}</div>
                </div>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => openEdit(t)} className="p-1.5 text-white/35 hover:text-[#4EBFBF] transition-colors"><Edit2 size={13} /></button>
                <button onClick={() => handleDelete(t.id)} className="p-1.5 text-white/35 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
              </div>
            </div>
            <p className="text-white/55 text-xs font-source-sans line-clamp-3 italic">"{t.quote_es}"</p>
            <div className="mt-3">
              <span className={`text-xs font-poppins font-semibold px-2 py-0.5 ${t.active ? 'bg-green-500/15 text-green-400' : 'bg-white/8 text-white/40'}`}>
                {t.active ? 'Activo' : 'Inactivo'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="testimonial-form-modal">
          <div className="bg-[#1A2639] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-white/8">
              <h2 className="font-poppins font-bold text-white">{editing ? 'Editar Testimonio' : 'Nuevo Testimonio'}</h2>
              <button onClick={() => setShowForm(false)} className="text-white/40 hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {[['client_name','Nombre Cliente',true],['company','Empresa',true],['photo','URL Foto (opcional)',false]].map(([k,l,r]) => (
                <div key={k}>
                  <label className="block text-white/50 text-xs mb-1.5 font-source-sans uppercase tracking-wide">{l}</label>
                  <input required={r} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} className="w-full bg-[#0F1C2E] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#7D468C]" />
                </div>
              ))}
              {[['quote_es','Cita ES',true],['quote_en','Cita EN',true]].map(([k,l,r]) => (
                <div key={k}>
                  <label className="block text-white/50 text-xs mb-1.5 font-source-sans uppercase tracking-wide">{l}</label>
                  <textarea rows={3} required={r} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} className="w-full bg-[#0F1C2E] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#7D468C] resize-none" />
                </div>
              ))}
              <div className="flex items-center gap-2">
                <input type="checkbox" id="active-t" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="accent-[#7D468C]" />
                <label htmlFor="active-t" className="text-white/60 text-sm font-source-sans">Activo (visible en el sitio)</label>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className="bg-[#F2785C] text-white px-6 py-2.5 font-poppins font-semibold text-sm hover:bg-[#FF8F75] disabled:opacity-50">
                  {saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear')}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="border border-white/20 text-white/60 px-6 py-2.5 font-poppins text-sm hover:border-white/40">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
